import { Download, RefreshCw } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { ErrorMessage } from '../../components/ErrorMessage'
import type {
  AggregatorNetworkDashboard,
  AggregatorPersonResponse,
  NetworkItemRow,
  NetworkProfileStatus,
} from '../../types/api'
import { STATUS, saveBlob, slug } from './portalUtils'

const PAGE_SIZE = 25

type OnError = (err: unknown, fallback: string) => string | null

const sum = (m?: Record<string, number> | null) => Object.values(m ?? {}).reduce((a, b) => a + b, 0)

/**
 * How many people someone is connected with: requests they sent that were accepted, plus requests they
 * received and accepted - the network's "accept" bucket on each side.
 */
const accepted = (sent?: Record<string, number> | null, received?: Record<string, number> | null) =>
  (sent?.accept ?? 0) + (received?.accept ?? 0)

/**
 * The Signals-DPG aggregator dashboard for one coordinator: status filter, paged list with profile
 * completion and connections, and the CSV export. Used by the coordinator for itself, and by its
 * organisation for each coordinator - the loaders say whose.
 */
export function NetworkOverview({
  load,
  exportCsv,
  exportName,
  onError,
  title = 'On the Signals network',
}: {
  load: (params: { page: number; limit: number; status?: string; refresh?: boolean }) => Promise<AggregatorNetworkDashboard>
  exportCsv: (status?: string) => Promise<Blob>
  exportName: string
  onError: OnError
  title?: string
}) {
  const [status, setStatus] = useState<NetworkProfileStatus | ''>('')
  const [page, setPage] = useState(1)
  const [data, setData] = useState<AggregatorNetworkDashboard | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [exporting, setExporting] = useState(false)

  /**
   * @param force ask the network to recompute rather than answer from its cache (kept up to an hour),
   *   so people just brought in show at once. On opening and on Refresh; not on every page or filter.
   */
  const refresh = useCallback(async (force = false) => {
    setLoading(true)
    setError(null)
    try {
      setData(await load({ page, limit: PAGE_SIZE, status: status || undefined, refresh: force || undefined }))
    } catch (err) {
      setData(null)
      setError(onError(err, 'The Signals network could not be reached'))
    } finally {
      setLoading(false)
    }
  }, [load, page, status, onError])

  // Fresh on opening; paging and filtering after that read the network's cache.
  const opened = useRef(false)
  useEffect(() => {
    void refresh(!opened.current)
    opened.current = true
  }, [refresh])

  async function doExport() {
    setExporting(true)
    try {
      saveBlob(await exportCsv(status || undefined), `${slug(exportName)}-network.csv`)
    } catch (err) {
      setError(onError(err, 'Could not export the list'))
    } finally {
      setExporting(false)
    }
  }

  const block = data?.dashboard ? Object.values(data.dashboard.by_domain)[0] : undefined
  const rollup = block?.rollup
  const total = block?.total_matching ?? 0

  return (
    <section className="card-pad">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-bold text-ink-900">{title}</h2>
          <p className="text-sm text-ink-500">
            Status is the network&apos;s own: New in their first week, Active while connecting, then At risk
            and Inactive as they go quiet.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => void refresh(true)} className="btn-ghost gap-1.5">
            <RefreshCw size={14} /> Refresh
          </button>
          <button onClick={() => void doExport()} disabled={!data?.available || exporting} className="btn-ghost gap-1.5 disabled:opacity-40">
            <Download size={14} /> {exporting ? 'Exporting...' : 'Export CSV'}
          </button>
        </div>
      </div>

      {error ? (
        <p className="mt-3 text-sm text-amber-700">{error}</p>
      ) : loading && !data ? (
        <p className="mt-3 text-sm text-ink-500">Loading...</p>
      ) : !data?.available ? (
        <p className="mt-3 text-sm text-ink-500">{data?.reason ?? 'Not available yet.'}</p>
      ) : (
        <>
          <div className="mt-4 flex flex-wrap gap-2 text-xs">
            <FilterChip label={`All · ${rollup?.total_items ?? 0}`} current={status === ''} onClick={() => { setStatus(''); setPage(1) }} />
            {STATUS.map((s) => (
              <FilterChip
                key={s.key}
                label={`${s.label} · ${rollup?.by_status?.[s.key] ?? 0}`}
                current={status === s.key}
                onClick={() => { setStatus(s.key); setPage(1) }}
              />
            ))}
            <span className="ml-auto self-center text-ink-500">
              Connected {accepted(rollup?.by_initiated_action_status, rollup?.by_received_action_status)} · requests sent{' '}
              {sum(rollup?.by_initiated_action_status)} · received {sum(rollup?.by_received_action_status)}
            </span>
          </div>

          {!block || block.items.length === 0 ? (
            <p className="mt-4 text-sm text-ink-500">
              Nobody here yet. People appear a little while after they are published (learners under 18 are
              not published).
            </p>
          ) : (
            <NetworkTable items={block.items} />
          )}

          {total > PAGE_SIZE && (
            <div className="mt-3 flex items-center justify-between text-sm text-ink-600">
              <span>
                {(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, total)} of {total}
              </span>
              <div className="flex gap-2">
                <button className="btn-ghost disabled:opacity-40" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
                  Previous
                </button>
                <button className="btn-ghost disabled:opacity-40" disabled={page * PAGE_SIZE >= total} onClick={() => setPage((p) => p + 1)}>
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  )
}

function NetworkTable({ items }: { items: NetworkItemRow[] }) {
  return (
    <div className="mt-4 overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead className="text-xs uppercase text-ink-400">
          <tr>
            <th className="py-2 pr-3">Name</th>
            <th className="py-2 pr-3">Status</th>
            <th className="py-2 pr-3">Profile</th>
            <th className="py-2 pr-3">Complete</th>
            <th className="py-2 pr-3">Sent</th>
            <th className="py-2 pr-3">Received</th>
            <th className="py-2 pr-3">Connected</th>
            <th className="py-2">Joined</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-ink-100">
          {items.map((item) => {
            const s = STATUS.find((x) => x.key === item.profile_status)
            return (
              <tr key={item.profile_item_id}>
                <td className="py-2 pr-3 font-medium text-ink-900">{item.name}</td>
                <td className="py-2 pr-3">{s ? <Pill className={s.className}>{s.label}</Pill> : '-'}</td>
                <td className="py-2 pr-3 capitalize text-ink-600">{item.lifecycle_status ?? '-'}</td>
                <td className="py-2 pr-3 text-ink-600">
                  {item.profile_completion_pct == null ? '-' : `${Math.round(item.profile_completion_pct)}%`}
                </td>
                <td className="py-2 pr-3 text-ink-600">{sum(item.initiated)}</td>
                <td className="py-2 pr-3 text-ink-600">{sum(item.received)}</td>
                <td className="py-2 pr-3 font-semibold text-ink-900">{accepted(item.initiated, item.received)}</td>
                <td className="py-2 text-ink-500">
                  {item.profile_created_at ? new Date(item.profile_created_at).toLocaleDateString() : '-'}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

/** The learners or tutors a coordinator brought in, as Yellow Dot knows them. */
export function PeopleTable({ people, isTutors }: { people: AggregatorPersonResponse[]; isTutors: boolean }) {
  if (people.length === 0) return <p className="mt-2 text-sm text-ink-500">Nobody yet.</p>
  return (
    <div className="mt-3 overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead className="text-xs uppercase text-ink-400">
          <tr>
            <th className="py-2 pr-3">Name</th>
            <th className="py-2 pr-3">Contact</th>
            <th className="py-2 pr-3">City</th>
            <th className="py-2 pr-3">{isTutors ? 'Teaches' : 'Looking for'}</th>
            {isTutors && <th className="py-2 pr-3">Review</th>}
            <th className="py-2 pr-3">Network</th>
            <th className="py-2">Added</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-ink-100">
          {people.map((p) => (
            <tr key={p.id}>
              <td className="py-2 pr-3 font-medium text-ink-900">{p.fullName}</td>
              <td className="py-2 pr-3 text-xs text-ink-600">
                {p.emailMasked ?? '-'}
                <br />
                {p.phoneMasked ?? ''}
              </td>
              <td className="py-2 pr-3 text-ink-600">{p.city ?? '-'}</td>
              <td className="py-2 pr-3 text-ink-600">{p.categories.length ? p.categories.join(', ') : '-'}</td>
              {isTutors && (
                <td className="py-2 pr-3">
                  {p.verificationStatus === 'VERIFIED' ? (
                    <Pill className="bg-green-100 text-green-800">Verified</Pill>
                  ) : p.verificationStatus === 'SUSPENDED' ? (
                    <Pill className="bg-red-100 text-red-800">Suspended</Pill>
                  ) : (
                    <Pill className="bg-amber-100 text-amber-800">In review</Pill>
                  )}
                </td>
              )}
              <td className="py-2 pr-3">
                {p.onNetwork ? (
                  <Pill className="bg-green-100 text-green-800">Published</Pill>
                ) : p.minor ? (
                  <Pill className="bg-ink-100 text-ink-600">Under 18</Pill>
                ) : (
                  <Pill className="bg-amber-100 text-amber-800">Pending</Pill>
                )}
              </td>
              <td className="py-2 text-ink-500">{new Date(p.createdAt).toLocaleDateString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** A people list that loads itself. */
export function PeopleSection({
  title,
  subtitle,
  isTutors,
  load,
  onError,
  reloadKey = 0,
}: {
  title: string
  subtitle?: string
  isTutors: boolean
  load: () => Promise<AggregatorPersonResponse[]>
  onError: OnError
  reloadKey?: number
}) {
  const [people, setPeople] = useState<AggregatorPersonResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    load()
      .then((rows) => {
        if (!cancelled) {
          setPeople(rows)
          setError(null)
        }
      })
      .catch((err) => {
        if (!cancelled) setError(onError(err, 'Could not load the list'))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [load, onError, reloadKey])

  return (
    <section className="card-pad">
      <h2 className="font-bold text-ink-900">
        {title} <span className="font-normal text-ink-500">({people.length})</span>
      </h2>
      {subtitle && <p className="text-xs text-ink-500">{subtitle}</p>}
      <ErrorMessage message={error} />
      {loading && people.length === 0 ? <p className="mt-2 text-sm text-ink-500">Loading...</p> : <PeopleTable people={people} isTutors={isTutors} />}
    </section>
  )
}

export function Tile({ icon, label, value }: { icon: React.ReactNode; label: string; value: number | string }) {
  return (
    <div className="card-pad">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-ink-500">
        <span className="text-ink-400">{icon}</span> {label}
      </div>
      <div className="mt-1 text-2xl font-extrabold text-ink-900">{value}</div>
    </div>
  )
}

export function TabButton({ icon, label, current, onClick }: { icon: React.ReactNode; label: string; current: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      aria-current={current ? 'page' : undefined}
      className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold transition ${
        current ? 'bg-ink-900 text-ink-50' : 'text-ink-600 hover:bg-ink-100'
      }`}
    >
      {icon} {label}
    </button>
  )
}

function FilterChip({ label, current, onClick }: { label: string; current: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`rounded-full px-2.5 py-1 font-semibold transition ${current ? 'bg-ink-900 text-ink-50' : 'bg-ink-100 text-ink-700 hover:bg-ink-200'}`}
    >
      {label}
    </button>
  )
}

export function Pill({ className, children }: { className: string; children: React.ReactNode }) {
  return <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${className}`}>{children}</span>
}
